import { PaginatedResponse, PaginationParams, Permission } from '../types';
import { apiClient } from './api';

export type Role = {
  id: string;
  name: string;
  slug: string;
  description: string;
  permissions: string[];
  adminCount: number;
  isSystem: boolean;
};

export type AdminAccount = {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  roleName: string;
  isActive: boolean;
  permissions?: string[];
  createdAt: string;
};

type PagePayload<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

function mapRole(raw: Record<string, unknown>): Role {
  const perms = Array.isArray(raw.permissions) ? (raw.permissions as string[]) : [];
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    slug: String(raw.slug ?? ''),
    description: String(raw.description ?? ''),
    permissions: perms,
    adminCount: Number(raw.adminCount ?? 0),
    isSystem: Boolean(raw.isSystem),
  };
}

function mapAdmin(raw: Record<string, unknown>): AdminAccount {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    email: String(raw.email ?? ''),
    phone: String(raw.phone ?? ''),
    role: String(raw.role ?? ''),
    roleName: String(raw.roleName ?? ''),
    isActive: Boolean(raw.isActive ?? true),
    permissions: Array.isArray(raw.permissions) ? (raw.permissions as string[]) : undefined,
    createdAt: String(raw.createdAt ?? ''),
  };
}

export const rbacService = {
  async listPermissions(): Promise<Permission[]> {
    const res = await apiClient.get<Array<Record<string, unknown>>>('/admin/permissions');
    if (!res.success || !res.data) throw new Error(res.message || res.error || 'خطا در دریافت دسترسی‌ها');
    return res.data.map((p) => ({
      id: String(p.id ?? p.slug ?? ''),
      name: String(p.name ?? ''),
      slug: String(p.slug ?? ''),
    }));
  },

  async listRoles(): Promise<Role[]> {
    const res = await apiClient.get<Array<Record<string, unknown>>>('/admin/roles');
    if (!res.success || !res.data) throw new Error(res.message || res.error || 'خطا در دریافت نقش‌ها');
    return res.data.map(mapRole);
  },

  async createRole(payload: {
    name: string;
    slug: string;
    description: string;
    permissions: string[];
  }): Promise<Role> {
    const res = await apiClient.post<Record<string, unknown>>('/admin/roles', payload);
    if (!res.success || !res.data) throw new Error(res.message || res.error || 'خطا در ایجاد نقش');
    return mapRole(res.data);
  },

  async updateRole(
    id: string,
    payload: { name: string; description: string; permissions: string[] }
  ): Promise<Role> {
    const res = await apiClient.put<Record<string, unknown>>(`/admin/roles/${id}`, payload);
    if (!res.success || !res.data) throw new Error(res.message || res.error || 'خطا در ویرایش نقش');
    return mapRole(res.data);
  },

  async deleteRole(id: string): Promise<void> {
    const res = await apiClient.delete(`/admin/roles/${id}`);
    if (!res.success) throw new Error(res.message || res.error || 'خطا در حذف نقش');
  },

  async getAdmins(params: PaginationParams = {}): Promise<PaginatedResponse<AdminAccount>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 20;
    const res = await apiClient.get<PagePayload<Record<string, unknown>>>(
      `/admin/admins?page=${page}&limit=${limit}`
    );
    if (!res.success || !res.data) throw new Error(res.message || res.error || 'خطا در دریافت ادمین‌ها');
    return {
      data: res.data.data.map(mapAdmin),
      total: res.data.total,
      page: res.data.page,
      limit: res.data.limit,
      totalPages: res.data.totalPages,
    };
  },

  async createAdmin(payload: {
    name: string;
    email: string;
    phone: string;
    password: string;
    role: string;
  }): Promise<AdminAccount> {
    const res = await apiClient.post<Record<string, unknown>>('/admin/admins', payload);
    if (!res.success || !res.data) throw new Error(res.message || res.error || 'خطا در ایجاد ادمین');
    return mapAdmin(res.data);
  },

  async updateAdmin(
    id: string,
    payload: {
      name: string;
      email: string;
      phone: string;
      password?: string;
      role: string;
      isActive: boolean;
    }
  ): Promise<AdminAccount> {
    const res = await apiClient.put<Record<string, unknown>>(`/admin/admins/${id}`, payload);
    if (!res.success || !res.data) throw new Error(res.message || res.error || 'خطا در ویرایش ادمین');
    return mapAdmin(res.data);
  },
};
